import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Upload, 
  LogOut, 
  Loader2, 
  CalendarCheck, 
  Instagram, 
  Users, 
  Mail, 
  Calendar, 
  Clock, 
  Images, 
  BookOpen, 
  Bell, 
  MessageCircle,
  Settings,
  Menu,
  ChevronRight
} from "lucide-react";
import { MonthlyCalendarTab } from "@/components/admin/MonthlyCalendarTab";
import { WeeklyGiraTab } from "@/components/admin/WeeklyGiraTab";
import { AppointmentSlotsTab } from "@/components/admin/AppointmentSlotsTab";
import { InstagramPostTab } from "@/components/admin/InstagramPostTab";
import { MembersTab } from "@/components/admin/MembersTab";
import { NewsletterTab } from "@/components/admin/NewsletterTab";
import { GalleryTab } from "@/components/admin/GalleryTab";
import LibraryTab from "@/components/admin/LibraryTab";
import { NotificationsTab } from "@/components/admin/NotificationsTab";
import { WhatsAppCampaignTab } from "@/components/admin/WhatsAppCampaignTab";
import { SettingsTab } from "@/components/admin/SettingsTab";

type TabType = 
  | "appointments" 
  | "members" 
  | "gallery" 
  | "library" 
  | "newsletter" 
  | "notifications" 
  | "whatsapp" 
  | "instagram" 
  | "calendar" 
  | "weekly"
  | "settings";

interface MenuItem {
  id: TabType;
  label: string;
  icon: React.ElementType;
  description: string;
}

const menuItems: MenuItem[] = [
  { id: "appointments", label: "Atendimentos", icon: CalendarCheck, description: "Gerenciar slots de agendamento" },
  { id: "members", label: "Membros", icon: Users, description: "Gerenciar membros e consulentes" },
  { id: "gallery", label: "Galeria", icon: Images, description: "Gerenciar álbuns e fotos" },
  { id: "library", label: "Biblioteca", icon: BookOpen, description: "Gerenciar livros e PDFs" },
  { id: "newsletter", label: "Comunicados", icon: Mail, description: "Enviar comunicados por email" },
  { id: "notifications", label: "Push", icon: Bell, description: "Enviar notificações push" },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, description: "Campanhas WhatsApp" },
  { id: "instagram", label: "Posts", icon: Instagram, description: "Gerar posts para Instagram" },
  { id: "calendar", label: "Calendário", icon: Calendar, description: "Calendário mensal de giras" },
  { id: "weekly", label: "Gira Semanal", icon: Clock, description: "Gira da semana" },
  { id: "settings", label: "Configurações", icon: Settings, description: "Configurações do sistema" },
];

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("appointments");
  const [sheetOpen, setSheetOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .eq("role", "admin")
          .single();

        if (roles) {
          setIsAuthenticated(true);
          setIsAdmin(true);
        } else {
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      }
    } catch (error) {
      console.error("Auth check error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", data.user.id)
          .eq("role", "admin")
          .single();

        if (roles) {
          setIsAuthenticated(true);
          setIsAdmin(true);
          toast({
            title: "Login realizado com sucesso",
            description: "Bem-vindo ao painel administrativo",
          });
        } else {
          await supabase.auth.signOut();
          toast({
            title: "Acesso negado",
            description: "Você não tem permissão de administrador",
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      toast({
        title: "Erro no login",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setIsAdmin(false);
    navigate("/");
  };

  const handleMenuClick = (tabId: TabType) => {
    setActiveTab(tabId);
    setSheetOpen(false);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "appointments":
        return <AppointmentSlotsTab />;
      case "members":
        return <MembersTab />;
      case "gallery":
        return <GalleryTab />;
      case "library":
        return <LibraryTab />;
      case "newsletter":
        return <NewsletterTab />;
      case "notifications":
        return <NotificationsTab />;
      case "whatsapp":
        return <WhatsAppCampaignTab />;
      case "instagram":
        return <InstagramPostTab />;
      case "calendar":
        return <MonthlyCalendarTab />;
      case "weekly":
        return <WeeklyGiraTab />;
      case "settings":
        return <SettingsTab />;
      default:
        return <AppointmentSlotsTab />;
    }
  };

  const activeMenuItem = menuItems.find(item => item.id === activeTab);

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 py-20">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-3xl font-display text-center text-primary">
                Painel Administrativo
              </CardTitle>
              <CardDescription className="text-center">
                Entre com suas credenciais de administrador
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-16 sm:py-20">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="max-w-5xl mx-auto">
            {/* Header with Menu Button */}
            <div className="flex justify-between items-center gap-4 mb-6 sm:mb-8 px-2">
              <div className="flex items-center gap-3">
                <h1 className="font-display text-2xl sm:text-4xl md:text-5xl text-primary font-bold">
                  Painel Admin
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="icon" className="relative">
                      <Menu className="w-5 h-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[320px] sm:w-[380px] p-0">
                    <SheetHeader className="p-6 pb-4 border-b bg-muted/30">
                      <SheetTitle className="text-xl font-display text-primary flex items-center gap-2">
                        <Menu className="w-5 h-5" />
                        Menu Admin
                      </SheetTitle>
                    </SheetHeader>
                    <nav className="p-3 space-y-1 max-h-[calc(100vh-120px)] overflow-y-auto">
                      {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeTab === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => handleMenuClick(item.id)}
                            className={`
                              w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200
                              ${isActive 
                                ? 'bg-primary text-primary-foreground shadow-md' 
                                : 'hover:bg-muted/80 text-foreground'
                              }
                            `}
                          >
                            <div className={`p-2 rounded-md ${isActive ? 'bg-primary-foreground/20' : 'bg-muted'}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="font-medium">{item.label}</div>
                              <div className={`text-xs ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                                {item.description}
                              </div>
                            </div>
                            <ChevronRight className={`w-4 h-4 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                          </button>
                        );
                      })}
                    </nav>
                  </SheetContent>
                </Sheet>
                <Button variant="outline" size="icon" onClick={handleLogout} title="Sair">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Current Tab Indicator */}
            {activeMenuItem && (
              <div className="mb-6 px-2">
                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border">
                  <div className="p-2 rounded-md bg-primary/10">
                    <activeMenuItem.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg">{activeMenuItem.label}</h2>
                    <p className="text-sm text-muted-foreground">{activeMenuItem.description}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Content */}
            <div className="px-2">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Admin;
